import { Popconfirm, Switch } from "antd";
import HubFilter from "components/shared-components/RemoteTable/HubFilter/HubFilter";
import CustomDateFilter from "components/shared-components/RemoteTable/custom-date-filter";
import {
  CUSTOM_DATE_FILTER_TABLE_TYPE,
  CUSTOM_SELECT_FILTER_TABLE_TYPE,
  DELAY_FILTER_TIME,
} from "constants/RemoteDataTableContanst";
import { useDistricts, useListTransport, useProvinces, useWards } from "hooks";
import {
  useDeleteTransportLocalities,
  useListTransportLocalities,
  useUpdateTransportLocalities,
} from "hooks/transportLocalities/useTransportLocalities";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import {
  customFilter,
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import BreadcrumbWithLink from "../../components/Common/BreadcrumbWithLink";
import ConfirmModal from "../../components/shared-components/ConfirmModal";
import RemoteDataTable from "../../components/shared-components/RemoteTable/data-table";
import { default as TransportLocalitiesForm } from "./TransportLocalitiesForm";
import { getInitTransportLocalitiesParams } from "./utils";
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from "reactstrap";
import { mapOptionObjectToArray } from "pages/CustomerManagement/Location/utils";
import TransportLocalitiesAuditLogsTable from "./TransportLocalitiesAuditLogsTable";
import { useTransportLocalitiesAuditLogs } from "hooks/audit-logs/useAuditLogs";

const ListTransportLocalities = props => {
  const { orderType } = props;
  const auditLogType =
    orderType === 1 ? "transport_locality_1" : "transport_locality_2";
  const auditLogHeaderText =
    orderType === 1
      ? "Audit Logs vùng trả kết quả"
      : "Audit Logs vùng nhận mẫu";

  const { t } = useTranslation(["transportLocalities"]);
  const [transportFilter, setTransportFilter] = useState(null);
  const [transports, setTransports] = useState([]);
  const [hubId, setHubId] = useState([]);
  const [addressLevel, setAddressLevel] = useState("all");
  const [addressLevelFilter, setAddressLevelFilter] = useState({
    value: "all",
    label: "Tất cả",
  });

  const [isOpenAddrFilter, setIsOpenAddrFilter] = useState(false);
  const [showAuditLogsModal, setShowAuditLogsModal] = useState(false);
  const [auditLogsTotalCount, setAuditLogsTotalCount] = useState(0);

  const { data: auditLogsData } = useTransportLocalitiesAuditLogs({
    type: auditLogType,
    enabled: showAuditLogsModal,
  });
  const addressLevelFilterOptions = [
    { value: "all", label: "Tất cả" },
    { value: "2", label: "2 cấp" },
    { value: "3", label: "3 cấp" },
  ];

  const showAllLevels = addressLevel === "all";

  const handleAddressLevelFilterChange = selectedOption => {
    if (addressLevelFilter.value === selectedOption.value) return;
    setAddressLevelFilter(selectedOption);
    setAddressLevel(selectedOption.value);

    setProvincesFilter(null);
    setDistrictsFilter(null);
    setWardsFilter(null);

    const currentFilter = { ...transportLocalitiesParams.columnFilter };
    const currentFilterKeep = { ...transportLocalitiesParams.columnFilterKeep };

    if (selectedOption.value === "2") {
      currentFilter["filter[is_new_address]"] = "N";
    } else if (selectedOption.value === "3") {
      currentFilter["filter[is_new_address]"] = "O";
    }

    setTransportLocalitiesParams(prev => ({
      ...prev,
      columnFilter: currentFilter,
      columnFilterKeep: currentFilterKeep,
      page: 1,
      run: true,
    }));
  };

  const [provincesFilter, setProvincesFilter] = useState(null);
  const [districtsFilter, setDistrictsFilter] = useState(null);
  const [wardsFilter, setWardsFilter] = useState(null);
  const selectedProvinceLevel = provincesFilter?.level;

  const useMergedProvinces = (showAllLevels, addressLevel) => {
    const [provinceParams2Level, setProvinceParams2Level] = useState({
      countryId: 1,
      type: 1,
      parent_code: "",
      record_status: "O",
    });

    const [provinceParams3Level, setProvinceParams3Level] = useState({
      countryId: 1,
      type: 1,
      parent_code: "",
      record_status: "C",
    });

    const { data: provinces2Level, refetch: refetchProvinces2Level } =
      useProvinces(provinceParams2Level);
    const { data: provinces3Level, refetch: refetchProvinces3Level } =
      useProvinces(provinceParams3Level);

    const mergedProvinces = useMemo(() => {
      const provinces2LevelArray = (
        mapOptionObjectToArray(provinces2Level) ?? []
      ).map(p => ({ ...p, level: 2 }));

      const provinces3LevelArray = (
        mapOptionObjectToArray(provinces3Level) ?? []
      ).map(p => ({ ...p, level: 3 }));

      if (showAllLevels) {
        const merged = [...provinces2LevelArray];
        provinces3LevelArray.forEach(p3 => {
          if (!merged.find(p => p.value === p3.value)) merged.push(p3);
        });

        return merged.sort((a, b) => {
          if (a.level !== b.level) {
            return a.level === 3 ? -1 : 1;
          }
          return a.label.localeCompare(b.label);
        });
      }

      const target =
        addressLevel === "2" ? provinces2LevelArray : provinces3LevelArray;
      return target.sort((a, b) => a.label.localeCompare(b.label));
    }, [provinces2Level, provinces3Level, showAllLevels, addressLevel]);

    useEffect(() => {
      if (showAllLevels) {
        refetchProvinces2Level();
        refetchProvinces3Level();
      } else if (addressLevel === "2") {
        refetchProvinces2Level();
      } else {
        refetchProvinces3Level();
      }
    }, [
      showAllLevels,
      addressLevel,
      refetchProvinces2Level,
      refetchProvinces3Level,
    ]);

    return mergedProvinces;
  };

  const onFetchTransportsSuccess = response => {
    const list = response?.items || [];
    const result = [];

    list.forEach(item => {
      result.push({ label: item.name, value: item.id });
    });
    setTransports(result);
  };

  const [transportParams, setTransportParams] = useState({
    sizePerPage: 10,
    page: 1,
    sort: "-maker_date",
    columnFilter: {},
    keywords: "",
    columnsVisible: {},
    fields: "",
    expand: "",
    run: false,
  });

  const { isFetching: isLoadingTransport, refetch: fetchTransports } =
    useListTransport(
      {
        params: {
          ...transportParams,
          columnFilter: {
            "filter[record_status]": "O",
            hub_ids: hubId.join(","),
          },
        },
      },
      onFetchTransportsSuccess
    );

  useEffect(() => {
    if (hubId) {
      setTransportParams(prev => ({
        ...prev,
        run: true,
      }));
    }
  }, [hubId, addressLevel]);

  useEffect(() => {
    if (transportParams && transportParams.run && hubId.length > 0) {
      fetchTransports();
    }
  }, [transportParams]);

  const {
    data: districts,
    refetch: refetchDistricts,
    isFetching: isFetchingDistricts,
  } = useDistricts(
    {
      countryId: 1,
      type: 2,
      parent_code: provincesFilter?.value || "",
      record_status: showAllLevels
        ? provincesFilter?.level === 2
          ? "O"
          : "C"
        : addressLevel === "2"
        ? "O"
        : "C",
    },
    null,
    null,
    false
  );

  useEffect(() => {
    if (provincesFilter?.value) {
      setDistrictsFilter(null);
      setWardsFilter(null);

      setTimeout(() => {
        refetchDistricts();
      });
    } else {
      setDistrictsFilter(null);
      setWardsFilter(null);
    }
  }, [provincesFilter?.value, provincesFilter?.level]);

  const {
    data: wards,
    refetch: refetchWards,
    isFetching: isFetchingWards,
  } = useWards(
    {
      countryId: 1,
      type: 3,
      parent_code: districtsFilter?.value || "",
      record_status: showAllLevels
        ? selectedProvinceLevel === 2
          ? "O"
          : "C"
        : addressLevel === "2"
        ? "O"
        : "C",
    },
    null,
    null,
    false
  );

  useEffect(() => {
    if (districtsFilter?.value) {
      setWardsFilter(null);

      setTimeout(() => {
        refetchWards();
      });
    } else {
      setWardsFilter(null);
    }
  }, [districtsFilter?.value, provincesFilter?.level]);

  const provinceOptions = useMergedProvinces(showAllLevels, addressLevel);

  const districtsOptions = useMemo(() => {
    if (districts) {
      const options = [];
      for (const key in districts) {
        options.push({
          label: districts[key],
          value: key,
        });
      }
      return options;
    }
    return [];
  }, [districts]);

  const wardsOptions = useMemo(() => {
    if (wards) {
      const options = [];
      for (const key in wards) {
        options.push({
          label: wards[key],
          value: key,
        });
      }
      return options;
    }
    return [];
  }, [wards]);

  const wardsOptionsFinal = useMemo(() => {
    const isProvince2Level = showAllLevels
      ? selectedProvinceLevel === 2
      : addressLevel === "2";

    if (isProvince2Level) {
      return districtsOptions;
    }
    return wardsOptions;
  }, [
    showAllLevels,
    selectedProvinceLevel,
    addressLevel,
    districtsOptions,
    wardsOptions,
  ]);

  const initColumns = [
    {
      dataField: "address_type",
      text: "Loại địa chỉ",
      sort: false,
      hidden: addressLevel !== "all",
      formatter: (_cell, row) => {
        const isRowLevel2 = row?.is_new_address === "N";
        return (
          <span
            className={`badge ${isRowLevel2 ? "bg-info" : "bg-secondary"}`}
            style={{
              fontSize: "11px",
              padding: "4px 8px",
              textAlign: "center",
              display: "inline-block",
            }}
          >
            {isRowLevel2 ? "2 cấp" : "3 cấp"}
          </span>
        );
      },
    },
    {
      dataField: "hub_id",
      text: "Center/Hub",
      sort: true,
      formatter: (cell, row) => {
        return row?.hub?.name;
      },
    },
    {
      dataField: "province_code",
      text: "Tỉnh thành",
      sort: false,
      allowExport: true,
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select")}
              options={provinceOptions}
              classNamePrefix="select2-selection"
              isLoading={false}
              value={provincesFilter}
              onChange={data => {
                if (!data) {
                  setProvincesFilter(null);
                  setDistrictsFilter(null);
                  setWardsFilter(null);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                  };
                  delete newFilter["filter[province_code]"];
                  delete newFilter["filter[district_code]"];
                  delete newFilter["filter[ward_code]"];

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                } else {
                  setProvincesFilter(data);
                  setDistrictsFilter(null);
                  setWardsFilter(null);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                    "filter[province_code]": data.value,
                  };

                  delete newFilter["filter[district_code]"];
                  delete newFilter["filter[ward_code]"];

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                }
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return row?.province?.name;
      },
    },
    {
      dataField: "district_code",
      text: "Quận huyện",
      sort: false,
      allowExport: true,
      hidden: addressLevel === "2",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        const isDisabled =
          !provincesFilter || (showAllLevels && selectedProvinceLevel === 2);

        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select")}
              options={districtsOptions}
              classNamePrefix="select2-selection"
              isLoading={isFetchingDistricts}
              isDisabled={isDisabled}
              value={districtsFilter}
              onChange={data => {
                if (!data) {
                  setDistrictsFilter(null);
                  setWardsFilter(null);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                  };
                  delete newFilter["filter[district_code]"];
                  delete newFilter["filter[ward_code]"];

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                } else {
                  setDistrictsFilter(data);
                  setWardsFilter(null);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                    "filter[district_code]": data.value,
                  };

                  delete newFilter["filter[ward_code]"];

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                }
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        if (row?.is_new_address === "N") {
          return "";
        }
        return row?.district?.name;
      },
    },
    {
      dataField: "ward_code",
      text: "Phường xã",
      sort: false,
      hidden: false,
      allowExport: true,
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        const isProvince2Level = showAllLevels
          ? selectedProvinceLevel === 2
          : addressLevel === "2";

        const isDisabled = isProvince2Level
          ? !provincesFilter
          : !districtsFilter;

        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select")}
              options={wardsOptionsFinal}
              classNamePrefix="select2-selection"
              isLoading={isFetchingWards}
              isDisabled={isDisabled}
              value={wardsFilter}
              onChange={data => {
                if (!data) {
                  setWardsFilter(null);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                  };

                  delete newFilter["filter[ward_code]"];

                  const isProvince2Level = showAllLevels
                    ? selectedProvinceLevel === 2
                    : addressLevel === "2";

                  if (isProvince2Level) {
                    delete newFilter["filter[district_code]"];
                  }

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                } else {
                  setWardsFilter(data);

                  const isProvince2Level = showAllLevels
                    ? selectedProvinceLevel === 2
                    : addressLevel === "2";

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                  };

                  if (isProvince2Level) {
                    newFilter["filter[district_code]"] = data.value;
                    delete newFilter["filter[ward_code]"];
                  } else {
                    newFilter["filter[ward_code]"] = data.value;
                  }

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                }
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        const isRowLevel2 = row?.is_new_address === "N";

        if (isRowLevel2) {
          return row?.district?.name;
        } else {
          return row?.ward?.name;
        }
      },
    },
    {
      dataField: "transport_id",
      text: "ĐVVC phân bổ",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select")}
              options={transports}
              classNamePrefix="select2-selection"
              isLoading={isLoadingTransport}
              value={transportFilter}
              onChange={data => {
                if (!data) {
                  setTransportFilter(null);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                  };
                  delete newFilter["filter[transport_id]"];

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                } else {
                  setTransportFilter(data);

                  const newFilter = {
                    ...transportLocalitiesParams.columnFilterKeep,
                    "filter[transport_id]": data.value,
                  };

                  if (addressLevel === "2") {
                    newFilter["filter[is_new_address]"] = "N";
                  } else if (addressLevel === "3") {
                    newFilter["filter[is_new_address]"] = "O";
                  }

                  setTransportLocalitiesParams(prev => ({
                    ...prev,
                    columnFilter: newFilter,
                    columnFilterKeep: newFilter,
                    page: 1,
                    run: true,
                  }));
                }
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return row?.transport?.name;
      },
    },
    {
      dataField: "is_noted",
      text: t("table.is_noted"),
      sort: true,
      align: "center",
      filter: selectFilter({
        placeholder: t("placeholder.select", {
          title: t("table.is_noted"),
        }),
        options: {
          0: "Không",
          1: "Có",
        },
      }),
      formatter: (cell, row) => (
        <Popconfirm
          title={`${cell === 1 ? "Tắt" : "Bật"} cần lưu ý ?`}
          onConfirm={() => handleActiveIsNoted(row)}
          okText="Ok"
          cancelText="Huỷ"
        >
          <Switch size="small" checked={cell === 1} />
        </Popconfirm>
      ),
    },
    {
      dataField: "checker_id",
      text: t("table.checker_id"),
      formatter: (cell, row) => {
        return row.checker && row.checker.display_name
          ? row.checker.display_name
          : cell;
      },
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        placeholder: t("placeholder.enter", {
          title: t("table.checker_id"),
        }),
      }),
      allowExport: true,
    },
    {
      dataField: "checker_date",
      text: t("table.checker_date"),
      sort: true,
      formatter: (cell, row) => {
        if (cell) {
          return new Intl.DateTimeFormat("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }).format(Number(cell) * 1000);
        } else {
          return "";
        }
      },
      filter: customFilter({
        type: CUSTOM_DATE_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => (
        <CustomDateFilter
          onFilter={onFilter}
          column={column}
          paramFilter={transportLocalitiesParams.columnFilter}
        />
      ),
    },
  ];

  const [columnsTable, setColumnsTable] = useState(initColumns);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalUpdate, setOpenModalUpdate] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);

  const [updatingTransportLocalities, setUpdatingTransportLocalities] =
    useState(null);
  const [deletingTransportLocalities, setDeletingTransportLocalities] =
    useState(null);
  const [transportLocalitiesParams, setTransportLocalitiesParams] = useState({
    ...getInitTransportLocalitiesParams,
    columnFilterKeep: {
      "filter[order_type]": orderType,
    },
  });

  const {
    data: transportLocalitiesList,
    isFetching: isLoadingList,
    refetch: fetchTransportLocalities,
  } = useListTransportLocalities({ params: transportLocalitiesParams });

  useEffect(() => {
    if (transportLocalitiesParams && transportLocalitiesParams?.run) {
      fetchTransportLocalities();
    }
  }, [transportLocalitiesParams.run, transportLocalitiesParams.columnFilter]);

  useEffect(() => {
    setTransportParams(prev => ({
      ...prev,
      page: -1,
    }));
  }, []);

  useEffect(() => {
    if (transportLocalitiesList) {
      initColumn();
    }
  }, [transportLocalitiesList]);

  useEffect(() => {
    initColumn();
  }, [transports, transportParams, districts, wards, addressLevel]);

  const initColumn = () => {
    setColumnsTable(initColumns);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter }
  ) => {
    const currentKeep = { ...transportLocalitiesParams.columnFilterKeep };

    const tableFilters = {};

    for (const key in columnFilter) {
      if (
        key !== "filter[province_code]" &&
        key !== "filter[district_code]" &&
        key !== "filter[ward_code]" &&
        key !== "filter[transport_id]"
      ) {
        if (columnFilter[key]) {
          tableFilters[key] = columnFilter[key];
        }
      }
    }

    const newFilter = {
      ...currentKeep,
      ...tableFilters,
    };

    newFilter["filter[order_type]"] = orderType;

    if (addressLevel === "2") {
      newFilter["filter[is_new_address]"] = "N";
    } else if (addressLevel === "3") {
      newFilter["filter[is_new_address]"] = "O";
    }

    setTransportLocalitiesParams(prev => ({
      ...prev,
      sort: sortField ? sortOrder + sortField : prev.sort,
      columnFilter: newFilter,
      columnFilterKeep: newFilter,
      page: page || 1,
      sizePerPage: sizePerPage || 10,
      run: true,
    }));
  };

  const onSearchAllHandle = value => {
    setTransportLocalitiesParams(prev => ({
      ...prev,
      keywords: value,
      run: true,
    }));
  };

  const { mutate: deleteTransportLocalities } = useDeleteTransportLocalities(
    () => {
      setTransportFilter(null);
      fetchTransportLocalities();
    }
  );
  const handleDelete = () => {
    deleteTransportLocalities(deletingTransportLocalities.id);
    setOpenModalDelete(false);
  };

  const { mutate: activeIsNoted } = useUpdateTransportLocalities(
    fetchTransportLocalities
  );

  const handleActiveIsNoted = row => {
    activeIsNoted({
      transportLocalitiesId: row.id,
      is_noted: row?.is_noted === 1 ? 0 : 1,
    });
    setOpenModalDelete(false);
  };

  const listRowAction = [
    {
      icon: <i className="fas fa-pen fa-lg text-primary" />,
      text: t("table.actions.update"),
      onClick: row => {
        setUpdatingTransportLocalities(row);
        setOpenModalUpdate(true);
      },
    },
    {
      icon: <i className="fas fa-trash fa-lg text-danger" />,
      text: t("table.actions.delete"),
      onClick: row => {
        setDeletingTransportLocalities(row);
        setOpenModalDelete(true);
      },
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>
            {t("title.list")}
            {t(`type_${orderType}`)}
          </title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={
              t("title.list") +
              ` ${t(`type_${orderType}`)}` +
              ` (${transportLocalitiesList?.totalCount || 0})`
            }
            breadcrumbItems={[
              { name: t("title.transportLocalities") },
              { name: t("title.list") + ` (${t(`type_${orderType}`)})` },
            ]}
          />
          <Modal
            size="lg"
            isOpen={openModalUpdate}
            centered
            toggle={() => {
              setOpenModalUpdate(!openModalUpdate);
            }}
          >
            <ModalHeader>Cập nhật phân bổ ĐVVC</ModalHeader>
            <TransportLocalitiesForm
              orderType={orderType}
              detailData={updatingTransportLocalities}
              onSuccess={() => {
                setOpenModalUpdate(false);
                fetchTransportLocalities();
              }}
            />
          </Modal>
          <Modal
            size="lg"
            isOpen={openModalCreate}
            centered
            toggle={() => {
              setOpenModalCreate(!openModalCreate);
            }}
          >
            <ModalHeader>Thêm mới phân bổ ĐVVC</ModalHeader>
            <TransportLocalitiesForm
              orderType={orderType}
              onSuccess={() => {
                setOpenModalCreate(false);
                fetchTransportLocalities();
              }}
            />
          </Modal>
          <Modal size="xl" isOpen={showAuditLogsModal} centered>
            <ModalHeader
              toggle={() => setShowAuditLogsModal(!showAuditLogsModal)}
            >
              {auditLogHeaderText} ({auditLogsTotalCount || 0})
            </ModalHeader>
            <ModalBody className="pt-0">
              <TransportLocalitiesAuditLogsTable
                type={auditLogType}
                onTotalCountChange={setAuditLogsTotalCount}
              />
            </ModalBody>
          </Modal>
          <ConfirmModal
            isOpen={openModalDelete}
            titleHeader={t("action.delete.title")}
            titleBody={`
                        ${t("action.delete.description", {
                          code: deletingTransportLocalities?.code,
                        })} ?`}
            onConfirm={handleDelete}
            textBtnConfirm={t("button.confirm")}
            textBtnCancel={t("button.cancel")}
            toggle={() => setOpenModalDelete(false)}
          />

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  {columnsTable.length > 0 && (
                    <RemoteDataTable
                      keyField="id"
                      data={transportLocalitiesList?.items || []}
                      columns={columnsTable}
                      page={transportLocalitiesParams.page}
                      sizePerPage={transportLocalitiesParams.sizePerPage}
                      totalSize={transportLocalitiesList?.totalCount || 0}
                      onTableChange={handleTableChange}
                      onSearchAllHandle={onSearchAllHandle}
                      listRowAction={listRowAction}
                      loading={isLoadingList}
                    >
                      <div className="d-flex gap-2 align-items-center">
                        <div style={{ minWidth: 100 }}>
                          <Dropdown
                            isOpen={isOpenAddrFilter}
                            toggle={() =>
                              setIsOpenAddrFilter(!isOpenAddrFilter)
                            }
                          >
                            <DropdownToggle
                              className="btn primary-btn w-100 text-overflow"
                              caret
                            >
                              Địa chỉ
                              <span>:&nbsp;{addressLevelFilter.label}</span>
                              <i className="mdi mdi-chevron-down" />
                            </DropdownToggle>
                            <DropdownMenu
                              className="w-100"
                              style={{ minWidth: 100 }}
                            >
                              {addressLevelFilterOptions.map(opt => (
                                <DropdownItem
                                  key={opt.value}
                                  onClick={() =>
                                    handleAddressLevelFilterChange(opt)
                                  }
                                >
                                  {opt.label}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </Dropdown>
                        </div>

                        <HubFilter
                          mainFilterParams={transportLocalitiesParams}
                          setMainFilterParams={setTransportLocalitiesParams}
                          handleSelectHub={hub_id_handle => {
                            setHubId(hub_id_handle);

                            const newFilter = {
                              "filter[order_type]": orderType,
                              "filter[hub_ids]": [],
                            };

                            if (addressLevel === "2") {
                              newFilter["filter[is_new_address]"] = "N";
                            } else if (addressLevel === "3") {
                              newFilter["filter[is_new_address]"] = "O";
                            }

                            setTransportLocalitiesParams(prev => ({
                              ...prev,
                              columnFilter: newFilter,
                              columnFilterKeep: newFilter,
                              page: 1,
                              run: true,
                            }));
                          }}
                          isNotFilterChildHub={true}
                          isHaveAllOption={true}
                          isFilterAllDefault={true}
                          keyFilterIn="hub_ids"
                          addressLevel={
                            addressLevel === "all"
                              ? 3
                              : addressLevel === "2"
                              ? 2
                              : 3
                          }
                        />

                        <Button
                          onClick={() => setOpenModalCreate(true)}
                          color="primary"
                          className="btn-label"
                          type="button"
                        >
                          <i className="fas fa-plus label-icon" />
                          {t("title.create")}
                        </Button>
                        <Button
                          onClick={() => setShowAuditLogsModal(true)}
                          color="primary"
                          className="btn-label me-2"
                          type="button"
                        >
                          <i className="fas fa-history label-icon" />
                          Audit Logs
                        </Button>
                      </div>
                    </RemoteDataTable>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

ListTransportLocalities.propTypes = {
  orderType: PropTypes.any,
};

export default ListTransportLocalities;
